import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";

export interface TrendPoint {
  /** تصنيف المحور الأفقي، مثال: "14:00" */
  label: string;
  /** إجمالي المبيعات للساعة */
  revenue: number;
  /** عدد الطلبات للساعة */
  count: number;
}

export type TrendMetric = "revenue" | "count" | "both";

interface SalesTrendChartProps {
  points: TrendPoint[];
  metric: TrendMetric;
}

export interface ChartExportOptions {
  filename?: string;
  /** اسم الفرع أو العنوان الرئيسي على رأس الصورة */
  title?: string;
  /** سطر فرعي: التاريخ والفترة والمقياس */
  subtitle?: string;
}

export interface SalesTrendChartHandle {
  /** يصدّر الرسم الحالي كصورة PNG قابلة للتنزيل */
  exportPng: (options?: ChartExportOptions) => Promise<void>;
}

const REVENUE_COLOR = "#2E9E46";
const COUNT_COLOR = "#C98A00";

const WIDTH = 640;
const HEIGHT = 240;
/** أقل عرض بالبكسل لكل ساعة على المحور الأفقي عشان النص يفضل مقروء */
const MIN_WIDTH_PER_POINT = 60;
/** أقل عرض إجمالي للرسم قبل ما نسمح بالسكرول الأفقي */
const MIN_CHART_WIDTH = 640;
const PAD_SINGLE = { top: 24, right: 18, bottom: 34, left: 48 };
const PAD_DUAL = { top: 24, right: 48, bottom: 34, left: 48 };

/** يحوّل مكوّن SVG إلى صورة PNG عالية الجودة وينزّلها على الجهاز */
async function exportSvgAsPng(
  svg: SVGSVGElement,
  options: ChartExportOptions = {},
  scale = 2,
): Promise<void> {
  const { filename = "sales-trend.png", title = "", subtitle = "" } = options;

  const vb = svg.viewBox.baseVal;
  const width = vb && vb.width ? vb.width : 640;
  const height = vb && vb.height ? vb.height : 240;

  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("width", String(width));
  clone.setAttribute("height", String(height));
  clone.removeAttribute("class");

  const serializer = new XMLSerializer();
  const source = `<?xml version="1.0" standalone="no"?>${serializer.serializeToString(clone)}`;
  const svgBlob = new Blob([source], { type: "image/svg+xml;charset=utf-8" });
  const svgUrl = URL.createObjectURL(svgBlob);

  try {
    const image = new Image();
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("تعذّر قراءة الرسم"));
      image.src = svgUrl;
    });

    const chartW = Math.round(width * scale);
    const chartH = Math.round(height * scale);
    const pad = 24 * scale;
    const titleSize = 22 * scale;
    const subtitleSize = 13 * scale;
    const gap = 8 * scale;
    const sepGap = 16 * scale;
    const hasHeader = Boolean(title || subtitle);

    let cursorY = pad;
    if (title) {
      cursorY += titleSize;
      if (subtitle) cursorY += gap;
    }
    if (subtitle) cursorY += subtitleSize;
    const chartTop = hasHeader ? cursorY + sepGap : 0;

    const canvas = document.createElement("canvas");
    canvas.width = chartW;
    canvas.height = chartTop + chartH;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("تعذّر إنشاء لوحة الرسم");

    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (hasHeader) {
      ctx.direction = "rtl";
      ctx.textAlign = "right";
      ctx.textBaseline = "top";
      let textY = pad;
      if (title) {
        ctx.font = `700 ${titleSize}px system-ui, "Segoe UI", Tahoma, sans-serif`;
        ctx.fillStyle = "#1F2430";
        ctx.fillText(title, canvas.width - pad, textY);
        textY += titleSize + (subtitle ? gap : 0);
      }
      if (subtitle) {
        ctx.font = `400 ${subtitleSize}px system-ui, "Segoe UI", Tahoma, sans-serif`;
        ctx.fillStyle = "#6B7280";
        ctx.fillText(subtitle, canvas.width - pad, textY);
      }
      ctx.strokeStyle = "#E5E7EB";
      ctx.lineWidth = Math.max(1, scale);
      ctx.beginPath();
      ctx.moveTo(pad, chartTop - sepGap / 2);
      ctx.lineTo(canvas.width - pad, chartTop - sepGap / 2);
      ctx.stroke();
    }

    ctx.drawImage(image, 0, chartTop, chartW, chartH);

    const pngBlob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
    if (!pngBlob) throw new Error("تعذّر توليد الصورة");

    const pngUrl = URL.createObjectURL(pngBlob);
    const link = document.createElement("a");
    link.href = pngUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(pngUrl);
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}

/** يقرّب أعلى قيمة لمقياس لطيف (1/2/5 * 10^n) */
function niceTop(rawMax: number, forceInt = false): { top: number; step: number } {
  const safe = Math.max(rawMax, 1);
  const target = safe / 4;
  const pow = Math.pow(10, Math.floor(Math.log10(target)));
  const norm = target / pow;
  let mult = 1;
  if (norm > 5) mult = 10;
  else if (norm > 2) mult = 5;
  else if (norm > 1) mult = 2;
  let step = mult * pow;
  if (forceInt && step < 1) step = 1;
  const top = Math.ceil(safe / step) * step;
  return { top, step };
}

interface SeriesDef {
  key: "revenue" | "count";
  color: string;
  unit: string;
  decimals: number;
  dashed: boolean;
  values: number[];
  ys: number[];
  pointsAttr: string;
}

const SalesTrendChart = forwardRef<SalesTrendChartHandle, SalesTrendChartProps>(
  function SalesTrendChart({ points, metric }, ref) {
  const [active, setActive] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  useImperativeHandle(ref, () => ({
    exportPng: async (options: ChartExportOptions = {}) => {
      if (svgRef.current) await exportSvgAsPng(svgRef.current, options);
    },
  }));

  const showRevenue = metric === "revenue" || metric === "both";
  const showCount = metric === "count" || metric === "both";
  const dual = metric === "both";

  const geom = useMemo(() => {
    const pad = dual ? PAD_DUAL : PAD_SINGLE;
    const plotW = WIDTH - pad.left - pad.right;
    const plotH = HEIGHT - pad.top - pad.bottom;
    const baseY = pad.top + plotH;
    const lastIndex = Math.max(1, points.length - 1);

    const maxRevenue = Math.max(1, ...points.map((p) => p.revenue));
    const maxCount = Math.max(1, ...points.map((p) => p.count));
    const revenueScale = niceTop(maxRevenue, false);
    const countScale = niceTop(maxCount, true);
    const leftScale = metric === "count" ? countScale : revenueScale;

    const xFor = (i: number) => pad.left + (i / lastIndex) * plotW;
    const yLeft = (v: number) => pad.top + plotH * (1 - v / leftScale.top);
    const yRevenue = (v: number) => pad.top + plotH * (1 - v / revenueScale.top);
    const yCount = (v: number) => pad.top + plotH * (1 - v / countScale.top);

    const leftTicks: number[] = [];
    for (let v = 0; v <= leftScale.top + 0.001; v += leftScale.step) leftTicks.push(v);
    const rightTicks: number[] = [];
    if (dual) for (let v = 0; v <= countScale.top + 0.001; v += countScale.step) rightTicks.push(v);

    return {
      pad,
      plotW,
      plotH,
      baseY,
      lastIndex,
      leftTicks,
      rightTicks,
      xFor,
      yLeft,
      yRevenue,
      yCount,
      labelEvery: Math.max(1, Math.ceil(points.length / 8)),
    };
  }, [points, metric, dual]);

  const series = useMemo<SeriesDef[]>(() => {
    const list: SeriesDef[] = [];
    if (showRevenue) {
      const ys = points.map((p) => geom.yRevenue(p.revenue));
      list.push({
        key: "revenue",
        color: REVENUE_COLOR,
        unit: "ر.س",
        decimals: 2,
        dashed: false,
        values: points.map((p) => p.revenue),
        ys,
        pointsAttr: ys.map((y, i) => `${geom.xFor(i).toFixed(1)},${y.toFixed(1)}`).join(" "),
      });
    }
    if (showCount) {
      const ys = points.map((p) => geom.yCount(p.count));
      list.push({
        key: "count",
        color: COUNT_COLOR,
        unit: "طلب",
        decimals: 0,
        dashed: dual,
        values: points.map((p) => p.count),
        ys,
        pointsAttr: ys.map((y, i) => `${geom.xFor(i).toFixed(1)},${y.toFixed(1)}`).join(" "),
      });
    }
    return list;
  }, [points, showRevenue, showCount, dual, geom]);

  if (points.length === 0) {
    return (
      <div className="text-center py-12">
        <i className="ri-line-chart-line text-4xl text-foreground-300"></i>
        <p className="mt-3 text-sm text-foreground-500">لا توجد بيانات مسجّلة في هذه الفترة</p>
      </div>
    );
  }

  const areaSeries = dual ? null : series[0];
  const areaPath =
    areaSeries && areaSeries.ys.length > 0
      ? `M ${geom.xFor(0).toFixed(1)},${geom.baseY.toFixed(1)} ` +
        areaSeries.ys.map((y, i) => `L ${geom.xFor(i).toFixed(1)},${y.toFixed(1)}`).join(" ") +
        ` L ${geom.xFor(areaSeries.ys.length - 1).toFixed(1)},${geom.baseY.toFixed(1)} Z`
      : "";

  const activePoint = active !== null ? points[active] : null;
  const activeX = active !== null ? geom.xFor(active) : 0;
  const activeY = active !== null ? Math.min(...series.map((s) => s.ys[active])) : 0;

  // كل ساعة تاخد حد أدنى للعرض، ولما الساعات تزيد الرسم يتوسّع ويسكرول بدل ما ينضغط
  const minChartWidth = Math.max(MIN_CHART_WIDTH, points.length * MIN_WIDTH_PER_POINT);
  // لو النقطة قريبة من أعلى الرسم نعرض التلميح تحتها عشان ما يتقصّش
  const activeAbove = activeY > 110;

  return (
    <div className="w-full">
      {dual && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mb-3">
          <span className="inline-flex items-center gap-2 text-xs font-semibold text-foreground-700">
            <span className="inline-block w-4 h-1 rounded-full" style={{ background: REVENUE_COLOR }}></span>
            مبلغ المبيعات
            <span className="text-foreground-400 font-normal">(المحور الأيسر)</span>
          </span>
          <span className="inline-flex items-center gap-2 text-xs font-semibold text-foreground-700">
            <span className="inline-block w-4 h-1 rounded-full" style={{ background: COUNT_COLOR }}></span>
            عدد الطلبات
            <span className="text-foreground-400 font-normal">(المحور الأيمن)</span>
          </span>
        </div>
      )}

      <div className="overflow-x-auto">
      <div className="relative" dir="ltr" style={{ minWidth: minChartWidth }}>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          className="w-full h-auto block overflow-visible"
          role="img"
          aria-label="اتجاه المبيعات وعدد الطلبات على مدار اليوم"
        >
          <defs>
            <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={areaSeries ? areaSeries.color : REVENUE_COLOR} stopOpacity="0.26" />
              <stop offset="100%" stopColor={areaSeries ? areaSeries.color : REVENUE_COLOR} stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Grid lines + left axis labels */}
          {geom.leftTicks.map((v) => {
            const y = geom.yLeft(v);
            return (
              <g key={`lt-${v}`}>
                <line
                  x1={geom.pad.left}
                  x2={WIDTH - geom.pad.right}
                  y1={y}
                  y2={y}
                  stroke="#EEF0F3"
                  strokeWidth={1}
                />
                <text
                  x={geom.pad.left - 8}
                  y={y + 3}
                  textAnchor="end"
                  fill="#9AA1AC"
                  style={{ fontSize: 10 }}
                >
                  {Math.round(v)}
                </text>
              </g>
            );
          })}

          {/* Right axis labels (dual mode) */}
          {dual &&
            geom.rightTicks.map((v) => {
              const y = geom.yCount(v);
              return (
                <text
                  key={`rt-${v}`}
                  x={WIDTH - geom.pad.right + 8}
                  y={y + 3}
                  textAnchor="start"
                  fill="#9AA1AC"
                  style={{ fontSize: 10 }}
                >
                  {Math.round(v)}
                </text>
              );
            })}

          {/* Area (single metric only) */}
          {areaPath && <path d={areaPath} fill="url(#trendFill)" />}

          {/* Lines */}
          {series.map((s) => (
            <polyline
              key={`line-${s.key}`}
              points={s.pointsAttr}
              fill="none"
              stroke={s.color}
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray={s.dashed ? "6 5" : undefined}
              vectorEffect="non-scaling-stroke"
            />
          ))}

          {/* X labels */}
          {points.map((p, i) =>
            i % geom.labelEvery === 0 || i === points.length - 1 ? (
              <text
                key={`xl-${p.label}`}
                x={geom.xFor(i)}
                y={HEIGHT - 10}
                textAnchor="middle"
                fill="#9AA1AC"
                style={{ fontSize: 10 }}
              >
                {p.label}
              </text>
            ) : null,
          )}

          {/* Points */}
          {series.map((s) => (
            <g key={`dots-${s.key}`}>
              {s.ys.map((y, i) => (
                <circle
                  key={`dot-${s.key}-${points[i].label}`}
                  cx={geom.xFor(i)}
                  cy={y}
                  r={active === i ? (dual ? 4.5 : 5.5) : dual ? 2.75 : 3.5}
                  fill="#FFFFFF"
                  stroke={s.color}
                  strokeWidth={active === i ? 3 : 2.5}
                  className="transition-all duration-150"
                />
              ))}
            </g>
          ))}

          {/* Active guide */}
          {active !== null && (
            <line
              x1={activeX}
              x2={activeX}
              y1={geom.pad.top}
              y2={geom.baseY}
              stroke="#C2C7D0"
              strokeWidth={1}
              strokeDasharray="3 3"
            />
          )}

          {/* Hover targets */}
          {points.map((p, i) => (
            <rect
              key={`hit-${p.label}`}
              x={geom.xFor(i) - geom.plotW / (2 * Math.max(1, geom.lastIndex))}
              y={geom.pad.top}
              width={Math.max(18, geom.plotW / Math.max(1, geom.lastIndex))}
              height={geom.plotH}
              fill="transparent"
              onMouseEnter={() => setActive(i)}
              onFocus={() => setActive(i)}
              tabIndex={-1}
            />
          ))}

          {/* Reset when leaving */}
          <rect
            x={0}
            y={0}
            width={WIDTH}
            height={HEIGHT}
            fill="transparent"
            onMouseLeave={() => setActive(null)}
            pointerEvents="none"
          />
        </svg>

        {/* Tooltip */}
        {activePoint && (
          <div
            className="absolute pointer-events-none z-10"
            style={{
              left: `${(activeX / WIDTH) * 100}%`,
              top: `${(activeY / HEIGHT) * 100}%`,
              transform: activeAbove ? "translate(-50%, -100%)" : "translate(-50%, 0)",
            }}
          >
            <div className={`rounded-btn bg-foreground-900 text-background-50 px-3 py-2 text-center whitespace-nowrap ${activeAbove ? "mb-2" : "mt-2"}`}>
              <p className="text-[11px] text-foreground-300" dir="ltr">
                {activePoint.label}
              </p>
              {series.map((s) => (
                <p
                  key={`tip-${s.key}`}
                  className="mt-0.5 text-xs font-bold flex items-center justify-center gap-1.5"
                  dir="ltr"
                >
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: s.color }}></span>
                  {s.values[active as number].toFixed(s.decimals)} {s.unit}
                </p>
              ))}
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
  },
);

export default SalesTrendChart;